import classnames from 'classnames';
import React, {ReactElement, useMemo} from 'react';
import {UrlObject} from 'url';

import Row from '../../common/Row';
import SquareButton from './buttons/SquareButton';

type Props = {
  page: number;
  pageCount: number;
  numPageLinks?: number;
  getPageUrl(page: number): string | UrlObject;
  className?: string;
};

const Pagination = ({page, pageCount, numPageLinks = 3, getPageUrl, className}: Props): ReactElement | null => {
  const pageNumbers = useMemo(() => {
    if (pageCount >= numPageLinks) {
      const startPage = Math.max(1, Math.min(pageCount - numPageLinks + 1, Math.floor(page - numPageLinks / 2) + 1));
      return Array(numPageLinks)
        .fill(null)
        .map((_, idx) => startPage + idx);
    }
    return Array(pageCount)
      .fill(null)
      .map((_, idx) => idx + 1);
  }, [numPageLinks, page, pageCount]);

  if (pageCount < 1) return null;

  return (
    <Row className={classnames('space-x-4 justify-center', className)}>
      {page !== 1 && <SquareButton variant="secondary" direction="left" href={getPageUrl(page - 1)} />}
      {pageNumbers.map((number, idx) => (
        <SquareButton
          key={`pagination-number-${idx}`}
          variant={number === page ? 'primary' : 'secondary'}
          disabled={number === page}
          lookEnabled
          href={getPageUrl(number)}
        >
          {number}
        </SquareButton>
      ))}
      {page !== pageCount && <SquareButton variant="secondary" direction="right" href={getPageUrl(page + 1)} />}
    </Row>
  );
};

export default Pagination;
